import { useTranslation } from 'react-i18next'

import { EMPTY_OBJECT } from '../../../../constants/common'
import { useInitData, useNotification } from '../../../../hooks'
import { isDraftCreateNewAction, useDraftCreationOpener } from '../../../Draft'
import {
  getRequestFormErrorNotification,
  RequestDraftCreateActionSelectModal,
  useRequestWizardDraftSession,
} from '../../../Request'
import {
  getIdwellToIdwellRequestFormSuccessNotification,
  getIdwellToIdwellServiceRequestCreationInitialValues,
} from '../../IdwellToIdwell.utils'
import { IdwellToIdwellRequestFormModal } from '../../IdwellToIdwellRequestFormModal'
import { IdwellToIdwellRequestWizardFormModal } from '../../IdwellToIdwellRequestWizardFormModal'

import {
  getCreateIdwellToIdwellRequestPayloadInitialValues,
  getCreateIdwellToIdwellRequestSubmitPayload,
} from './useCreateIdwellToIdwellRequest.utils'

export function useCreateIdwellToIdwellRequest({
  boundUser,
  outboundUuid,
  permissions,
  setModal,
  onClose,
  onReload,
}) {
  const { t } = useTranslation()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { availableSections } = useInitData()

  const isWizardEnabled = !!permissions?.can_create_with_wf

  const {
    draft: activeDraft,
    draftId: activeDraftId,
    handleCreateNew,
    handleOpenDraft,
    minimizeKey,
  } = useRequestWizardDraftSession({ isWizardEnabled })

  function closeModal() {
    return setModal(null)
  }

  function handleErrorCreation() {
    handleAddErrorNotification(getRequestFormErrorNotification(t))
  }

  function handleSuccess(data) {
    onClose()

    handleAddSuccessNotification(
      getIdwellToIdwellRequestFormSuccessNotification({
        availableSections,
        data,
        outboundUuid,
        t,
      })
    )

    onReload?.()
  }

  function handleWizardSuccess(createdRequests) {
    createdRequests.forEach(createdRequest => {
      handleAddSuccessNotification(
        getIdwellToIdwellRequestFormSuccessNotification({
          availableSections,
          data: createdRequest,
          outboundUuid,
          t,
        })
      )
    })

    onReload?.()
  }

  function openModal({ action, formProps, wizardProps } = EMPTY_OBJECT) {
    setModal(
      isWizardEnabled ? (
        <IdwellToIdwellRequestWizardFormModal
          {...wizardProps}
          autoRestore={action != null && !isDraftCreateNewAction(action)}
          initialUser={boundUser}
          outboundUuid={outboundUuid}
          onClose={onClose}
          onError={handleErrorCreation}
          onSuccess={handleWizardSuccess}
        />
      ) : (
        <IdwellToIdwellRequestFormModal
          {...formProps}
          initialValues={
            formProps?.initialValues ??
            getIdwellToIdwellServiceRequestCreationInitialValues({
              boundUser,
            })
          }
          outboundUuid={outboundUuid}
          onClose={onClose}
          onError={handleErrorCreation}
          onSuccess={handleSuccess}
        />
      )
    )
  }

  const handleOpenCreation = useDraftCreationOpener({
    DraftPickerModal: RequestDraftCreateActionSelectModal,
    closeModal,
    draft: activeDraft,
    draftId: activeDraftId,
    getInitialValues: getCreateIdwellToIdwellRequestPayloadInitialValues,
    getSubmitPayload: getCreateIdwellToIdwellRequestSubmitPayload,
    minimizeKey,
    openModal,
    setModal,
    onCreateNew: handleCreateNew,
    onOpenDraft: handleOpenDraft,
  })

  return {
    handleOpenCreation,
    isWizardEnabled,
  }
}
